// What you are meant to be doing, and how far along you are.
//
// A four-hundred-square board with three armies on it has no natural focus,
// and a sequence of objectives is what supplies one (PLAN §12.1.1). That makes
// this a legibility mechanism rather than decoration — a run where the player
// cannot read the objective is a run where the board is just large.
//
// It sits under the turn row now (PLAN-UI §28.3, screen-mock `#objs`) rather
// than dodging a dock, so it takes no layout props at all. **It registers with
// `edge: null`**: it appears, grows and disappears with a run, and moving the
// camera because an objective completed is the thing §28.3 forbids — a
// selection-independent floating piece contributes nothing to the insets.
//
// Reads `SessionView.objectives` (PLAN-CLIENT-SPLIT day 8): which objective
// is active, which are behind you and how far along each is are the rules'.

import { Panel, SectionTitle, styled, useChromeRect } from "./kit";
import { useSessionView } from "./useSessionView";

const Card = styled(Panel)(({ theme }) => ({
  maxWidth: 320,
  padding: theme.kit.space.md,
  fontSize: 12.5,
  // Nothing here is clickable, and the board underneath stays live.
  pointerEvents: "none"
}));

const Line = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "done"
})<{ done?: boolean }>(({ theme, done }) => ({
  display: "flex",
  gap: theme.kit.space.sm,
  padding: "2px 0",
  lineHeight: 1.45,
  color: done ? theme.kit.color.inkDim : theme.kit.color.ink,
  textDecoration: done ? "line-through" : "none"
}));

const Mark = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "done"
})<{ done?: boolean }>(({ theme, done }) => ({
  color: done ? theme.kit.color.inkDim : theme.kit.color.brand,
  fontWeight: 700,
  fontStyle: "normal"
}));

/** A bar only where there is something to count. */
const Track = styled("span")(({ theme }) => ({
  display: "block",
  height: 3,
  borderRadius: 2,
  marginTop: 6,
  marginLeft: theme.kit.space.lg,
  background: theme.kit.color.line,
  overflow: "hidden"
}));

const Fill = styled("span")(({ theme }) => ({
  display: "block",
  height: "100%",
  background: theme.kit.color.brand
}));

const Caption = styled("span")(({ theme }) => ({
  display: "block",
  marginLeft: theme.kit.space.lg,
  fontFamily: theme.kit.font.display,
  fontSize: 10,
  color: theme.kit.color.inkDim
}));

export function ObjectivePanel() {
  const root = useChromeRect("objectives", null);
  const { objectives } = useSessionView();

  // **Nothing to be doing, nothing on the board.** This panel is an
  // instruction, so with no active objective it has none to give — what it
  // showed instead was a struck-through list under a permanent "Level
  // complete", which is a sticker rather than a thing to read. The view is
  // null outside a run and when there is nothing left to do.
  //
  // Nothing is lost by going quiet: an objective completing already announces
  // itself, a cleared front is a `Celebration`, and a won run is `RunOver`.
  if (!objectives) return null;

  return (
    <Card ref={root} data-testid="objectives">
      {/* First, and it stays first: the run smoke reads the level's name off
          the panel's first line of text. */}
      <SectionTitle>{objectives.level}</SectionTitle>

      {/* Only the objective you are on, and the ones behind you — the view
          already leaves out what is coming, since the next one is usually a
          spoiler for the region it opens. */}
      {objectives.lines.map((line) => (
        <div key={line.id} data-testid={`objective-${line.id}`}>
          <Line done={line.done}>
            <Mark done={line.done}>{line.done ? "✓" : "▸"}</Mark>
            <span>{line.text}</span>
          </Line>

          {line.current && line.need > 1 && (
            <>
              <Track>
                {/* `style`, because it changes: an `sx` value mints a
                    generated class per distinct value, permanently. */}
                <Fill style={{ width: `${Math.round((line.have / line.need) * 100)}%` }} />
              </Track>
              <Caption>
                {line.have} of {line.need}
              </Caption>
            </>
          )}
        </div>
      ))}
    </Card>
  );
}
